import type { ReactNode, Ref } from 'react';
import { Box, Typography } from '@mui/material';

export interface DimensionBlockProps {
  /** Section heading (translated copy) — e.g. "Data", "Voice", "Messaging". */
  title: string;
  /** The block body — typically the Unlimited/Pooled toggles + an IncludedValueField. */
  children: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

/**
 * A bordered card grouping one plan usage dimension: an uppercase title header
 * over a body that hosts the dimension's controls (the Unlimited/Pooled toggle
 * pair and the gated {@link IncludedValueField}). Presentational only — the
 * form owns the RHF wiring and the gating logic — so it is reusable by any
 * plan-shaped form (this slice's custom-plan editor, and later plan-facing
 * screens).
 */
export function DimensionBlock({ title, children, ref }: DimensionBlockProps) {
  return (
    <Box
      ref={ref}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: '14px',
          py: '10px',
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          component="h4"
          sx={{
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: 'text.primary',
            m: 0,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: '14px', display: 'flex', flexDirection: 'column', gap: 1.75 }}>{children}</Box>
    </Box>
  );
}
